import React, { useState } from 'react';
import { Modal, Form, Input, Select, InputNumber, Row, Col } from 'antd';
import { useTranslation } from 'react-i18next';
import { ApartmentUnit } from '@/types/resources/property';
import { BlockFloorSelector } from '@/components/common/BlockFloorSelector';
import { ResidentSelector } from '@/components/common/ResidentSelector';

interface ApartmentUnitFormModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (values: Partial<ApartmentUnit>) => void;
  initialValues?: Partial<ApartmentUnit>;
  title: string;
}

export const ApartmentUnitFormModal: React.FC<ApartmentUnitFormModalProps> = ({
  visible,
  onCancel,
  onSubmit,
  initialValues,
  title
}) => {
  const { t } = useTranslation('properties');
  const [form] = Form.useForm();
  
  // State to store selected data from selectors
  const [selectedBlockData, setSelectedBlockData] = useState<{ id: string; name: string } | null>(null);
  const [selectedFloorData, setSelectedFloorData] = useState<{ name: string; slug: string } | null>(null);
  const [selectedResidentData, setSelectedResidentData] = useState<{ id: string; name: string } | null>(null);

  const handleSubmit = () => {
    form.validateFields().then(values => {
      const { blockId, floorSlug, residentId, ...rest } = values;
      
      // Transform the form values to match the new structure using selected data
      // Only include defined values to avoid Firestore errors
      const transformedValues: any = {
        ...rest,
      };

      // Only add block if it's selected
      if (selectedBlockData) {
        transformedValues.block = selectedBlockData;
      }

      // Only add floor if it's selected
      if (selectedFloorData) {
        transformedValues.floor = selectedFloorData;
      }

      // Only add resident if it's selected
      if (selectedResidentData) {
        transformedValues.resident = selectedResidentData;
      }

      onSubmit({
        ...initialValues,
        ...transformedValues
      });
      form.resetFields();
      
      // Reset selected data
      setSelectedBlockData(null);
      setSelectedFloorData(null);
      setSelectedResidentData(null);
    });
  };

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          ...initialValues,
          blockId: initialValues?.block?.id,
          floorSlug: initialValues?.floor?.slug,
          residentId: initialValues?.resident?.id
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="number"
              label={t('apartmentUnits.details.attributes.number')}
              rules={[{ required: true, message: t('common.fieldRequired') }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <ResidentSelector
              excludeAssigned={true}
              onResidentDataChange={setSelectedResidentData}
            />
          </Col>
        </Row>

        {/* Block and Floor Selection */}
        <BlockFloorSelector
          onBlockDataChange={setSelectedBlockData}
          onFloorDataChange={setSelectedFloorData}
        />

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="bedrooms"
              label={t('apartmentUnits.details.attributes.bedrooms')}
            >
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="bathrooms"
              label={t('apartmentUnits.details.attributes.bathrooms')}
            >
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="area"
              label={t('apartmentUnits.details.attributes.area')}
            >
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="status"
          label={t('apartmentUnits.details.attributes.status')}
          rules={[{ required: true, message: t('common.fieldRequired') }]}
        >
          <Select>
            <Select.Option value="occupied">{t('apartmentUnits.status.occupied')}</Select.Option>
            <Select.Option value="available">{t('apartmentUnits.status.available')}</Select.Option>
            <Select.Option value="maintenance">{t('apartmentUnits.status.maintenance')}</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="description"
          label={t('common.description')}
        >
          <Input.TextArea rows={4} />
        </Form.Item>
      </Form>
    </Modal>
  );
}; 